import { ELEMENT_STATUSES } from '@/const/const.ts'
import { getActiveRole } from '@/lib/iterations/useIterationActiveRole.ts'
import { cn } from '@/lib/utils.ts'
import { IterationElement } from '@/services/firebase_shared_types.ts'
import { ChevronDown, LucideLink } from 'lucide-react'
import { useState } from 'react'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { useIsUserSuperAdmin } from '@/stores/useStore'
import { Button } from '@/components/ui/button.tsx'
import { IconStatus, MStatusIconBox, ROLE_REGISTRY_STATUS } from './MStatusIconBox.tsx'
import { useSearchParams } from 'react-router-dom'
import { useClipboardCopy } from '@/lib/hooks'
import { useToast } from '@/components/ui/use-toast'

export function MElement({ element }: MCompactElementProps) {
  const isSuperAdmin = useIsUserSuperAdmin()
  const [isExpanded, setIsExpanded] = useState(false)
  const roleRegistry = getActiveRole(element.role_registry)
  const isActiveRole = roleRegistry?.status === 'waiting_for_message'
  const status = (isActiveRole ? ROLE_REGISTRY_STATUS : element.status) as IconStatus
  const [_, setSearchParams] = useSearchParams()
  const { copy: copyUrl } = useClipboardCopy()
  const { toast } = useToast()

  if (!isSuperAdmin && element.status === ELEMENT_STATUSES.AWAITING) {
    return null
  }

  const updateUrl = () => {
    setSearchParams(
      prevParams => {
        const newParams = new URLSearchParams(prevParams)
        if (element?.index) {
          newParams.set('element', element.index.toString())
        } else {
          newParams.delete('element')
        }
        newParams.delete('step')
        newParams.delete('action')
        return newParams
      },
      { replace: false }
    )
  }

  const handleDeepLinkAction = () => {
    updateUrl()

    copyUrl(
      window.location.href,
      () =>
        toast({
          title: 'Element link copied to clipboard! 🎉',
        }),
      () =>
        toast({
          variant: 'destructive',
          title: 'Failed to copy element link to clipboard 😔',
          description: 'Check console for details and try again.',
        })
    )
  }

  return (
    <Collapsible
      open={isExpanded}
      onOpenChange={setIsExpanded}
      className={cn(
        'w-full animate-appear rounded border text-base-foreground transition-colors',
        isExpanded ? 'breder-border bg-white' : 'bg-transparents border-transparent'
      )}
    >
      <div
        className="group flex w-full cursor-pointer flex-row items-center gap-4 p-1"
        onClick={updateUrl}
      >
        <MStatusIconBox status={status} />
        <div className="flex flex-1 items-center justify-between">
          <h3 className="">
            <span
              className={cn(
                'font-pp-supply-sans',
                status === ELEMENT_STATUSES.FAILED ? 'text-red-400' : 'text-muted-foreground'
              )}
            >
              {element.index}.
            </span>{' '}
            <span
              className={cn(
                'text-sm font-medium',
                status === ELEMENT_STATUSES.FAILED
                  ? 'text-red-500'
                  : isExpanded || status === ELEMENT_STATUSES.RUNNING
                    ? 'text-base-foreground'
                    : 'text-stone-500'
              )}
            >
              {element.name}
            </span>
          </h3>
          <div className="flex h-8 items-center justify-center rounded-md bg-white/0">
            {isSuperAdmin && (
              <Button
                variant="ghost"
                className="px-2 opacity-0 transition-opacity group-hover:opacity-100"
                onClick={handleDeepLinkAction}
              >
                <LucideLink className="size-4" />
              </Button>
            )}
            {element.description && (
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="icon">
                  <ChevronDown
                    className={cn(
                      'size-4 text-muted-foreground transition-transform',
                      isExpanded && 'rotate-180'
                    )}
                  />
                </Button>
              </CollapsibleTrigger>
            )}
          </div>
        </div>
      </div>

      {element.description && (
        <CollapsibleContent>
          <div className="px-5 py-4 text-sm leading-tight">{element.description}</div>
        </CollapsibleContent>
      )}
    </Collapsible>
  )
}

type MCompactElementProps = {
  element: IterationElement
}
